import { useState, useEffect } from 'react';
import { Play } from 'lucide-react';
import { youtubeEmbedSrc } from '../../lib/youtubeEmbed';
import { youtubeThumbnail, youtubeWatchUrl, fetchYouTubeInfo } from '../../lib/youtubeLinks';

// Preview card for a YouTube video linked in a post or reply. Thumbnail and
// title, tap to play in place — the same embed path VideoScreen uses, so the
// Capacitor shim for iOS applies here too.
//
// No server involved: the thumbnail is a predictable image URL and the title
// comes from YouTube's CORS-enabled oEmbed endpoint. That is why this is
// YouTube-only; a preview for an arbitrary site needs a server-side fetcher
// (and SSRF protection), which is a separate project.
//
// If the title lookup fails (private or deleted video), the card still shows
// the thumbnail with a neutral label rather than disappearing, because the
// link in the text above it is still there and still says YouTube.
const YouTubePreview = ({ videoId, className = '' }) => {
  const [info, setInfo] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPlaying(false);
    setInfo(null);
    fetchYouTubeInfo(videoId).then((result) => { if (!cancelled) setInfo(result); });
    return () => { cancelled = true; };
  }, [videoId]);

  const title = info?.title ?? 'YouTube video';

  return (
    <div
      className={`rounded-xl overflow-hidden ${className}`}
      style={{ border: '1px solid var(--card-border)', backgroundColor: 'var(--card-bg)' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative aspect-video bg-black">
        {playing ? (
          <iframe
            src={youtubeEmbedSrc(videoId)}
            title={title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 w-full h-full"
            aria-label={`Play ${title}`}
          >
            <img
              src={youtubeThumbnail(videoId)}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/25 transition-colors">
              <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
                <Play size={20} fill="currentColor" style={{ color: 'var(--brand-primary)', marginLeft: 2 }} />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="px-3 py-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-primary line-clamp-2 leading-snug">{title}</p>
          {info?.author && <p className="text-xs text-tertiary truncate mt-0.5">{info.author}</p>}
        </div>
        <a
          href={youtubeWatchUrl(videoId)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium flex-shrink-0 hover:underline"
          style={{ color: 'var(--brand-primary)' }}
        >
          Watch on YouTube
        </a>
      </div>
    </div>
  );
};

export default YouTubePreview;
